import { useCallback, useEffect, useRef } from 'react'

interface UseScrollToBottomOptions {
  onScrollToBottom: () => void
  enabled?: boolean // Whether the hook should be active (default: false)
  threshold?: number // Distance from bottom in pixels (default: 50)
}

const useScrollToBottom = ({
  onScrollToBottom,
  enabled = false,
  threshold = 50,
}: UseScrollToBottomOptions) => {
  const hasTriggered = useRef(false)

  const handleScroll = useCallback(() => {
    // Don't check if already triggered or no window (extra safety)
    if (hasTriggered.current || typeof window === 'undefined') return

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const windowHeight = window.innerHeight
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    )

    // Check if user has scrolled to within threshold pixels of the bottom
    if (scrollTop + windowHeight >= documentHeight - threshold) {
      hasTriggered.current = true
      onScrollToBottom()

      // Remove event listener after triggering
      window.removeEventListener('scroll', handleScroll)
    }
  }, [onScrollToBottom, threshold])

  useEffect(() => {
    // Reset trigger on page load/mount (once per page load)
    hasTriggered.current = false

    // Don't add event listener if disabled or no window (SSG safety)
    if (!enabled || typeof window === 'undefined') {
      return
    }

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Cleanup function
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, enabled])
}

export default useScrollToBottom
